/** ArollSettings.tsx — workspace settings (Workspace / Account / Plan /
 *  Notifications tabs). */
export const settings = {
  /** Page heading; also the header context label KG staff see. */
  'settings.title': 'Settings',
  /** Toast when the initial settings fetch fails. */
  'settings.loadError': 'Could not load workspace settings',
  /** Inline indicator while a rename is being saved. */
  'settings.saving': 'Saving',
  /** Button label while an email is being sent. */
  'settings.sending': 'Sending',
  'settings.loading': 'Loading…',

  // ---- Tab pills -------------------------------------------------------
  'settings.tab.workspace': 'Workspace',
  'settings.tab.account': 'Account',
  'settings.tab.plan': 'Plan',
  'settings.tab.notifications': 'Notifications',

  // ---- Checkout return toasts -----------------------------------------
  'settings.checkout.success': 'Subscription active. Welcome aboard.',
  'settings.checkout.cancelled': 'Checkout cancelled.',

  // ---- Workspace tab ---------------------------------------------------
  /** Section heading. */
  'settings.workspace.title': 'Workspace',
  /** aria-label on the workspace name input. */
  'settings.workspace.nameAria': 'Workspace name',
  'settings.workspace.renamed': 'Workspace renamed',
  /** alt text on the uploaded logo image. */
  'settings.workspace.logoAlt': 'Workspace logo',
  /** Placeholder shown in the logo box when no logo is uploaded. */
  'settings.workspace.logoNone': 'None',
  'settings.workspace.upload': 'Upload',
  /** Button label while the logo upload is in flight. */
  'settings.workspace.uploading': 'Uploading',
  'settings.workspace.logoTooLarge': 'Logo must be under 1MB',
  'settings.workspace.logoUpdated': 'Logo updated',
  /** Hint under the logo uploader. "on dark" = on a dark background. */
  'settings.workspace.logoHint':
    'Shown on the pages you share with your clients. PNG or SVG on transparent reads best on dark.',
  'settings.workspace.clientAiTitle': 'Clients and AI',
  'settings.workspace.clientAi': 'Let clients use AI tools',
  'settings.workspace.clientAiBlurb':
    'People on your share links can use AI search (Find a moment) on A-Roll. Their use counts toward your workspace\'s AI allowance. Off by default.',
  'settings.workspace.clientAiOwnerOnly': 'Only the workspace owner can change this.',

  // ---- Account tab -----------------------------------------------------
  /** Section heading; also the aria-label on the name input. */
  /** Interface-language picker (Account tab). */
  'settings.account.language': 'Language',
  'settings.account.languageHint':
    'Applies to this workspace and the emails we send you. Captions and transcripts follow the footage, not this setting.',
  'settings.account.fullName': 'Full name',
  'settings.account.yourNamePlaceholder': 'Your name',
  'settings.account.nameUpdated': 'Name updated',
  /** Heading above the signed-in email address. */
  'settings.account.signedInAs': 'Signed in as',
  /** {name} is the workspace name. */
  'settings.account.workspaceLine': 'Workspace: {name}',
  'settings.account.changeEmailTitle': 'Change login email',
  'settings.account.changeEmailHint':
    'We email a confirmation link to the new address. Nothing changes until you click it.',
  /** Shown after the confirmation email is sent; {email} is the new address. */
  'settings.account.confirmationSent':
    'Check {email} for the confirmation link. It is valid for one hour.',
  'settings.account.newEmailPlaceholder': 'New email address',
  /** Placeholder + aria-label on current-password inputs. */
  'settings.account.currentPasswordPlaceholder': 'Current password',
  /** aria-label on the password input inside the email-change form. */
  'settings.account.currentPasswordForEmailAria': 'Current password for email change',
  'settings.account.sendConfirmation': 'Send confirmation',
  'settings.account.changePasswordTitle': 'Change password',
  'settings.account.newPasswordPlaceholder': 'New password',
  'settings.account.passwordHint':
    'At least 10 characters. Changing it signs you out everywhere.',
  /** Validation error before submit. */
  'settings.account.pwTooShort': 'New password needs at least 10 characters.',
  /** Toast after a password change; the user is walked to the login page. */
  'settings.account.pwUpdated': 'Password updated. Sign in with your new password.',
  /** Button label while the change is in flight. */
  'settings.account.updating': 'Updating',
  'settings.account.updatePassword': 'Update password',
  'settings.account.resetTitle': 'Reset by email',
  'settings.account.resetHint':
    'Forgotten your current password? We can email you a reset link instead.',
  'settings.account.sendResetEmail': 'Send reset email',
  /** Toast; {email} is the signed-in address the link went to. */
  'settings.account.resetLinkSent': 'Reset link sent to {email}',

  // ---- Plan tab --------------------------------------------------------
  'settings.plan.usageTitle': 'Usage this month',
  /** Meter row label: transcription minutes used. */
  'settings.plan.transcription': 'Transcription',
  /** Meter counter; {used} and {allowance} are minute counts. */
  'settings.plan.minutesUsage': '{used} / {allowance} min',
  /** Meter row label: AI draft generations used. */
  'settings.plan.aiDrafts': 'AI drafts',
  'settings.plan.billingTitle': 'Billing',
  /** Fallback plan name in the status line when the server sends none. */
  'settings.plan.fallbackName': 'Plan',
  /** Status line: cancelled but paid up. {plan} is the plan name,
   *  {date} a localised date (or the periodEndFallback text). */
  'settings.plan.cancelledLine':
    '{plan} cancelled. Access until {date}, and you will not be charged again.',
  /** Slots into {date} of cancelledLine when no date is known. */
  'settings.plan.periodEndFallback': 'the end of the period',
  /** Status line: active with a known renewal date. */
  'settings.plan.activeRenewsLine': '{plan} active, renews {date}',
  /** Status line: active, renewal date unknown. */
  'settings.plan.activeLine': '{plan} active',
  'settings.plan.pastDueLine': 'Payment issue. Update your card to keep uploading.',
  /** Status line: trial with a known end date. */
  'settings.plan.trialEndsLine': 'Trial, ends {date}',
  /** Status line: trial, end date unknown. */
  'settings.plan.trialLine': 'Trial',
  'settings.plan.canceledLine': 'Subscription ended. Choose a plan to keep uploading.',
  'settings.plan.freeLine': 'You are on the Free plan: client review included, one AI generation to try. Upgrade when the AI earns it.',
  'settings.plan.trialEndedLine': 'Trial ended. Choose a plan to keep uploading.',
  'settings.plan.notConfigured': 'Billing is not switched on in this environment yet.',
  /** Button label while the Stripe page is being opened. */
  'settings.plan.opening': 'Opening',
  'settings.plan.resubscribe': 'Resubscribe',
  'settings.plan.manageBilling': 'Manage billing',
  'settings.plan.updateCard': 'Update card',
  /** Checkout buttons: plan name + monthly price. Keep the figures;
   *  translate only the words if any. The currency is NOT in these
   *  strings — it is printed once, by currencyNote below. */
  'settings.plan.review': 'Review · ${n}/mo',
  'settings.plan.solo': 'Solo · ${n}/mo',
  /** One line under the checkout buttons. {currency} is an ISO code
   *  (AUD, USD) and stays as-is. */
  'settings.plan.currencyNote': 'Prices in {currency}.',
  'settings.plan.taxIncl': "Incl. GST.",
  'settings.plan.taxExcl': "Plus GST.",
  'settings.plan.studio': 'Studio · ${n}/mo',
  'settings.plan.agency': 'Agency · ${n}/mo',
  /** Monthly / annual switch above the checkout buttons. {plan} is a
   *  product name and stays as-is; {n} is the annual rate expressed
   *  per month, which is what the button shows. */
  'settings.plan.cadenceLabel': 'Billing period',
  'settings.plan.monthly': 'Monthly',
  'settings.plan.annualSave': 'Yearly · save 17%',
  'settings.plan.annualBtn': '{plan} · ${n}/mo billed yearly',
  /** Shown beside an active plan so the renewal date reads correctly. */
  'settings.plan.billedAnnually': 'Billed yearly',
  'settings.plan.includesTitle': 'Your plan includes',
  // "Your plan includes" bullets. {n} is always a number from the plan.
  'settings.plan.inc.minutesMonthly': '{n} transcription minutes / month',
  'settings.plan.inc.minutesFree': '{n} one-time transcription minutes',
  'settings.plan.inc.draftsFree': 'One AI generation to try the edit assistant',
  'settings.plan.inc.minutesTrial': '{n} trial transcription minutes',
  'settings.plan.inc.drafts': '{n} AI drafts / month',
  'settings.plan.inc.reviewMonthly': '{n} Playback review minutes / month',
  'settings.plan.inc.reviewTrial': '{n} trial Playback review minutes',
  'settings.plan.inc.retention': '{n}-day footage retention',
  'settings.plan.inc.speakers': 'Speaker identification and labels',
  /** Brief, Story, Tighten are product mode names — keep them verbatim. */
  'settings.plan.inc.draftModes': 'Brief, Story and Tighten AI draft modes',
  /** Transcript and Arrange are product surface names — keep verbatim. */
  'settings.plan.inc.editing': 'Transcript and Arrange editing',
  /** Resolve, Premiere, FCPX are edit-software names — keep verbatim. */
  'settings.plan.inc.export': 'EDL and marker export for Resolve, Premiere and FCPX',
  'settings.plan.inc.sharePages': 'Client share pages with notes and approval',
  'settings.plan.inc.keepBeyond': 'Transcripts, drafts and exports kept beyond retention',

  // ---- Notifications tab -----------------------------------------------
  'settings.notif.title': 'Email notifications',
  'settings.notif.hint':
    'Saved as you flip them. Account and billing email (verification, password resets, usage warnings) always sends.',
  /** Master toggle row (same English as the section title on purpose). */
  'settings.notif.master': 'Email notifications',
  'settings.notif.masterBlurb': 'The master switch. Off silences every notification email.',
  'settings.notif.projectUpdates': 'Project updates',
  'settings.notif.projectUpdatesBlurb': 'Notes, approvals and changes on your projects.',
  'settings.notif.productNews': 'New features',
  'settings.notif.productNewsBlurb': 'Occasional notes when PictureUp learns a new trick.',
} as const;
